// frontend/src/pages/admin/EventApproval.js
import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';

export default function EventApproval() {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);

  useEffect(() => { fetchEvents(); }, []);

  // Fetch events using admin/events with pagination (fetch first page of pending)
  const fetchEvents = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/admin/events', { params: { page: 1, limit: 100 } });
      const rows = res.data && res.data.rows ? res.data.rows : res.data || [];
      setEvents(rows.filter(e => e.status === 'Pending'));
    } catch (err) {
      console.error(err);
      setError(err.userMessage || err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const selectAll = () => {
    const s = new Set(events.map(e => e.id));
    setSelected(s);
  };

  const clearSelection = () => setSelected(new Set());

  const singleUpdate = async (id, status) => {
    setError(null);
    try {
      await api.put(`/admin/event-status/${id}`, { status });
      await fetchEvents();
    } catch (err) {
      console.error(err);
      setError(err.userMessage || err.message || 'Failed to update status');
    }
  };

  const startBulk = (action) => {
    if (!selected.size) { setError('Please select at least one event'); return; }
    setBulkAction(action); setConfirmOpen(true);
  };

  const confirmBulk = async () => {
    setConfirmOpen(false);
    setError(null);
    try {
      const ids = Array.from(selected);
      await api.put('/admin/events/bulk-status', { ids, status: bulkAction });
      clearSelection();
      await fetchEvents();
    } catch (err) {
      console.error(err);
      setError(err.userMessage || err.message || 'Bulk update failed');
    }
  };

  return (
    <AdminLayout>
      <h2 className="text-xl font-bold mb-4">Event Approval</h2>

      <div className="mb-4 flex items-center gap-3">
        <button className="btn btn-sm" onClick={selectAll}>Select all</button>
        <button className="btn btn-sm" onClick={clearSelection}>Clear</button>
        <button className="btn btn-sm btn-primary" onClick={() => startBulk('Approved')}>Bulk Approve</button>
        <button className="btn btn-sm" style={{ background: 'linear-gradient(90deg,#ef4444,#fb7185)', color: '#fff' }} onClick={() => startBulk('Cancelled')}>Bulk Cancel</button>
      </div>

      {error && <div className="text-red-600 mb-2">{error}</div>}

      <table className="w-full border-collapse bg-white shadow rounded">
        <thead>
          <tr className="bg-gray-200 text-left">
            <th className="p-3" style={{ width: 40 }}></th>
            <th className="p-3">Name</th>
            <th className="p-3">Date</th>
            <th className="p-3">Manager</th>
            <th className="p-3">Status</th>
            <th className="p-3">Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={6} className="p-4">Loading...</td></tr>
          ) : events.length === 0 ? (
            <tr><td colSpan={6} className="p-4">No pending events.</td></tr>
          ) : (
            events.map(e => (
              <tr key={e.id} className="border-b">
                <td className="p-3"><input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSelect(e.id)} /></td>
                <td className="p-3">{e.name}</td>
                <td className="p-3">{e.date}</td>
                <td className="p-3">{e.manager_name}</td>
                <td className="p-3">{e.status}</td>
                <td className="p-3">
                  <button className="bg-green-600 text-white px-3 py-1 rounded mr-2" onClick={() => singleUpdate(e.id, 'Approved')}>Approve</button>
                  <button className="bg-red-600 text-white px-3 py-1 rounded" onClick={() => singleUpdate(e.id, 'Cancelled')}>Cancel</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <ConfirmModal
        open={confirmOpen}
        title={bulkAction === 'Approved' ? 'Confirm Bulk Approve' : 'Confirm Bulk Cancel'}
        message={`Are you sure you want to ${bulkAction === 'Approved' ? 'approve' : 'cancel'} ${selected.size} selected events?`}
        onConfirm={confirmBulk}
        onCancel={() => setConfirmOpen(false)}
        confirmLabel={bulkAction === 'Approved' ? 'Approve' : 'Cancel'}
      />
    </AdminLayout>
  );
}
